import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Slider,
  Avatar,
  Divider,
  Tab,
  Tabs,
  Paper,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Search as SearchIcon,
  Tune as FilterIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import apiClient from "../../services/apiClient";
import PageLoader from "../Loading/PageLoader";



const VoiceLibrary = () => {
  const [loading, setLoading] = useState(false);
  const [voices, setVoices] = useState([]);
  const [allVoices, setAllVoices] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // const [selectedTab, setSelectedTab] = useState(0);

  // Dynamic providers and genders from API
  const [dynamicProviders, setDynamicProviders] = useState([]);
  const [dynamicGenders, setDynamicGenders] = useState([]);

  const [filters, setFilters] = useState({
    provider: "all",
    accent: "all",
    gender: "all",
  });
  const [audioSettings, setAudioSettings] = useState({
    stability: 0.5,
    similarity_boost: 0.75,
    speed: 1.0,
  });
  const [playingVoice, setPlayingVoice] = useState(null);
  const [audioElement, setAudioElement] = useState(null);


  useEffect(() => {
    fetchLanguages();
    fetchAllVoices();
  }, []);

  const fetchAllVoices = async () => {
    try {
      setLoading(true);

      const res = await apiClient.get("/voice-configs");

      if (res?.data) {
        const list = res.data;
        setAllVoices(list);
        setVoices(list);

        // Extract Unique Providers
        const providerList = [
          "all",
          ...new Set(list.map((v) => v.provider).filter(Boolean)),
        ];

        // Extract Unique Genders
        const genderList = [
          "all",
          ...new Set(list.map((v) => v.gender).filter(Boolean)),
        ];

        setDynamicProviders(providerList);
        setDynamicGenders(genderList);
      } else {
        toast.error("No voices found");
      }
    } catch (err) {
      console.error("Error fetching all voices:", err);
      toast.error("Failed to fetch voices");
    } finally {
      setLoading(false);
    }
  };



  const fetchFilteredVoices = async () => {
    setLoading(true);

    try {
      let url = "";
      let params = {};
      let useClientSideFiltering = false;

      // Determine which API to call based on active filters
      // Priority: Use the most specific API endpoint available
      if (filters.provider !== "all" && filters.accent !== "all") {
        // Use search endpoint if both provider and accent are selected
        url = `/voice-configs/search`;
        params = {
          provider: filters.provider.toLowerCase(),
          accent: filters.accent
        };
        useClientSideFiltering = filters.gender !== "all";
      } else if (filters.accent !== "all") {
        // Use accent endpoint
        url = `/voice-configs/accent`;
        params = { accent: filters.accent };
        useClientSideFiltering = filters.gender !== "all" || filters.provider !== "all";
      } else if (filters.gender !== "all") {
        // Use gender endpoint
        url = `/voice-configs/gender`;
        params = { gender: filters.gender };
        useClientSideFiltering = filters.provider !== "all";
      } else if (filters.provider !== "all") {
        // Use provider endpoint
        url = `/voice-configs/provider`;
        params = { provider: filters.provider };
      } else {
        // No specific filter, show all
        setVoices(allVoices);
        setLoading(false);
        return;
      }

      const response = await apiClient.get(url, params);

      if (response?.status && response?.data) {
        let filteredData = response.data;

        // Apply additional client-side filters for combinations not handled by the API
        if (useClientSideFiltering) {
          if (filters.gender !== "all") {
            filteredData = filteredData.filter(v => v.gender === filters.gender);
          }
          if (filters.provider !== "all" && url !== `/voice-configs/provider` && url !== `/voice-configs/search`) {
            filteredData = filteredData.filter(v =>
              v.provider.toLowerCase() === filters.provider.toLowerCase()
            );
          }
        }

        setVoices(filteredData);
        toast.success(response.message || "Voices fetched successfully");
      } else {
        toast.error("No voices found");
        setVoices([]);
      }
    } catch (error) {
      console.error("Error fetching filtered voices:", error);
      toast.error("Error fetching filtered voices");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (filters.provider !== "all" || filters.accent !== "all" || filters.gender !== "all") {
      fetchFilteredVoices();
    } else {
      setVoices(allVoices);
    }
  }, [filters]);

  // const handleToggleFavorite = (voiceId) => {
  //   setVoices(
  //     voices.map((v) =>
  //       v._id === voiceId ? { ...v, is_favorite: !v.is_favorite } : v
  //     )
  //   );
  //   toast.success("Favorites updated");
  // };

  const fetchLanguages = async () => {
    try {
      const res = await apiClient.get("/languages");

      if (res?.data) {
        const apiLanguages = res.data.map((item) => ({
          name: item.language_name,
        }));

        setLanguages([{ name: "all" }, ...apiLanguages]);
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      toast.error("Failed to load languages");
    }
  };

  const handlePlay = (voice) => {
    // Stop currently playing audio
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    if (playingVoice === voice._id) {
      setPlayingVoice(null);
      setAudioElement(null);
      // toast.info("Stopped");
      toast("Stopped", { icon: '⏹️' });
    } else {
      const audio = new Audio(`${process.env.REACT_APP_BASE_URL}/${voice.voice_sample}`);
      audio.play();
      setAudioElement(audio);
      setPlayingVoice(voice._id);
      toast.success(`Playing: ${voice.voice_name}`);

      // Auto stop when audio ends
      audio.onended = () => {
        setPlayingVoice(null);
        setAudioElement(null);
      };
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
  };

  const handleClearFilters = () => {
    setFilters({ provider: "all", accent: "all", gender: "all" });
    setSearchQuery("");
    setVoices(allVoices);
  };

  // Filter voices based on search query
  const filteredVoices = voices.filter((voice) => {
    const matchesSearch =
      voice.voice_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      voice.accent?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      voice.voice_code?.toLowerCase().includes(searchQuery.toLowerCase());



    return matchesSearch;
  });

  // const favoriteCount = voices.filter((v) => v.is_favorite).length;

  // if (loading && voices.length === 0) {
  //   return <PageLoader text="Loading voices..." />;
  // }

  return (
    
    <>

      {loading && voices.length === 0 && <PageLoader />}

      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            p: 3,
            mb: 3,
            border: "1px solid #E5E7EB",
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "#1F2937", fontWeight: 600, mb: 1 }}
          >
            Voice Library
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280" }}>
            Explore and preview AI voices from multiple providers
          </Typography>
        </Box>

        {/* Tabs */}
        {/* <Paper sx={{ mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant="fullWidth"
            >
              <Tab label={`All Voices (${voices.length})`} />
              <Tab label={`Favorites (${favoriteCount})`} />
            </Tabs>
          </Paper> */}

        {/* Filters Section */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 3, display:"flex", alignItems:"center", gap:"20px", }}>

            <Box
              sx={{
                display: "flex", flex:"0 0 1",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FilterIcon sx={{ color: "#667eea" }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#1F2937" }}>
                Filters
              </Typography>
            </Box>

            <Grid container spacing={2} sx={{ width: "#667eea" }}>
              {/* Search */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search voices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                  size="small"
                />
              </Grid>

              {/* Provider */}
              <Grid item xs={12} md={2}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                  size="small"
                >
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={filters.provider}
                    label="Provider"
                    onChange={(e) => handleFilterChange("provider", e.target.value)}
                  >
                    {dynamicProviders.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p === "all" ? "All" : p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Languages */}
              <Grid item xs={12} md={2}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                  size="small"
                >
                  <InputLabel>Languages</InputLabel>
                  <Select
                    value={filters.accent}
                    label="Languages"
                    onChange={(e) => handleFilterChange("accent", e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.name} value={lang.name}>
                        {lang.name === "all" ? "All" : lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Gender */}
              <Grid item xs={12} md={2}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#667eea",
                    },
                  }}
                  size="small"
                >
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={filters.gender}
                    label="Gender"
                    onChange={(e) => handleFilterChange("gender", e.target.value)}
                  >
                    {dynamicGenders.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g === "all" ? "All" : g}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Clear Filters Button */}
              <Grid item xs={12} md={3}>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{
                    width: "150px",
                    height: "40px",
                    borderRadius: "8px",
                    borderColor: "#667eea",
                    color: "#667eea",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#667eea",
                      backgroundColor: "#F3F4FF",
                    },
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Voice Cards */}
        <Box>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <PageLoader text="Loading voices..." />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredVoices.map((voice) => (
                <Grid item xs={12} sm={6} md={3} key={voice._id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "12px",
                      border: "1px solid #E5E7EB",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                        borderColor: "#667eea",
                      },
                      opacity: voice.status ? 1 : 0.6,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor:
                              voice.gender === "Female"
                                ? "#ec4899"
                                : "#667eea",
                            width: 48,
                            height: 48,
                            fontWeight: 600,
                          }}
                        >
                          {voice.voice_name?.charAt(0) || "V"}
                        </Avatar>
                        {/* <IconButton
                          size="small"
                          onClick={() => handleToggleFavorite(voice._id)}
                        >
                          {voice.is_favorite ? (
                            <FavoriteIcon color="error" />
                          ) : (
                            <FavoriteBorderIcon />
                          )}
                        </IconButton> */}
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1F2937",
                          mb: 1,
                        }}
                      >
                        {voice.voice_name}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          mb: 2,
                          minHeight: 40,
                        }}
                      >
                        {voice.voice_code}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
                        <Chip
                          label={voice.provider}
                          size="small"
                          sx={{
                            backgroundColor: "#F3F4FF",
                            color: "#667eea",
                            fontWeight: 500,
                            border: "1px solid #E5E7EB",
                          }}
                        />
                        <Chip
                          label={voice.gender}
                          size="small"
                          sx={{
                            backgroundColor: "#FFF",
                            color: "#6B7280",
                            fontWeight: 500,
                            border: "1px solid #E5E7EB",
                          }}
                        />
                        <Chip
                          label={voice.accent}
                          size="small"
                          sx={{
                            backgroundColor: "#FFF",
                            color: "#6B7280",
                            fontWeight: 500,
                            border: "1px solid #E5E7EB",
                          }}
                        />
                      </Box>

                      {voice.style && (
                        <Chip
                          label={voice.style}
                          size="small"
                          sx={{
                            backgroundColor: "#FEF3C7",
                            color: "#92400E",
                            fontWeight: 500,
                            border: "1px solid #FDE68A",
                          }}
                        />
                      )}

                      {!voice.status && (
                        <Chip
                          label="Inactive"
                          size="small"
                          sx={{
                            mt: 1,
                            backgroundColor: "#FEE2E2",
                            color: "#991B1B",
                            fontWeight: 500,
                            border: "1px solid #FECACA",
                          }}
                        />
                      )}
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth
                        variant={playingVoice === voice._id ? "contained" : "outlined"}
                        startIcon={
                          playingVoice === voice._id ? <StopIcon /> : <PlayIcon />
                        }
                        onClick={() => handlePlay(voice)}
                        disabled={!voice.voice_sample}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: 600,
                          ...(playingVoice === voice._id
                            ? {
                                backgroundColor: "#ef4444",
                                color: "#FFF",
                                boxShadow: "none",
                                "&:hover": {
                                  backgroundColor: "#dc2626",
                                  boxShadow: "none",
                                },
                              }
                            : {
                                borderColor: "#667eea",
                                color: "#667eea",
                                "&:hover": {
                                  borderColor: "#667eea",
                                  backgroundColor: "#F3F4FF",
                                },
                              }),
                        }}
                      >
                        {playingVoice === voice._id ? "Stop" : "Preview"}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {!loading && filteredVoices.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                No voices found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
    
  );
};

export default VoiceLibrary;
